import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { slugify } from "../../lib/format";
import { toast } from "sonner";
import { Save, Send, Calendar, Trash2 } from "lucide-react";
import RichEditor from "../../components/RichEditor";
import ImageUpload from "../../components/ImageUpload";
import DateTimePicker from "../../components/DateTimePicker";

const empty = {
  title: "", subtitle: "", slug: "", content: "", cover_image: "", images: [],
  video_url: "", category_id: "", author_id: "", tags: [], status: "draft",
  featured: false, breaking: false, scheduled_at: "",
  seo_title: "", seo_description: "", seo_keywords: "",
};

export default function ArticleEditor() {
  const { id } = useParams();
  const isNew = id === "nova";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [imgInput, setImgInput] = useState("");
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data || []));
    api.get("/authors").then(r => setAuthors(r.data || []));
    if (!isNew) {
      // we need to fetch by id; use list with id filter via admin list
      api.get("/articles", { params: { public: false, limit: 500 } }).then(r => {
        const found = (r.data.items || []).find(a => a.id === id);
        if (found) {
          setForm({
            ...empty,
            ...found,
            tags: found.tags || [],
            images: found.images || [],
          });
        } else {
          toast.error("Notícia não encontrada");
          navigate("/admin/noticias");
        }
        setLoading(false);
      });
    }
  }, [id, isNew, navigate]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onTitleBlur = () => {
    if (isNew && !form.slug && form.title) update("slug", slugify(form.title));
    if (!form.seo_title && form.title) update("seo_title", form.title);
  };

  const addTag = () => { if (tagInput.trim()) { update("tags", [...form.tags, tagInput.trim()]); setTagInput(""); } };
  const removeTag = (i) => update("tags", form.tags.filter((_, x) => x !== i));
  const removeImage = (i) => update("images", form.images.filter((_, x) => x !== i));

  const save = async (status) => {
    if (!form.title || !form.content) { toast.error("Título e conteúdo são obrigatórios"); return; }
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      status,
      scheduled_at: status === "scheduled" ? form.scheduled_at : null,
      category_id: form.category_id || null,
      author_id: form.author_id || null,
    };
    try {
      if (isNew) {
        const { data } = await api.post("/articles", payload);
        toast.success("Notícia criada!");
        navigate(`/admin/noticias/${data.id}`);
      } else {
        await api.patch(`/articles/${id}`, payload);
        toast.success("Notícia atualizada!");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar");
    }
  };

  if (loading) return <AdminLayout><p className="font-sans-ui text-zinc-500">Carregando...</p></AdminLayout>;

  const canPublish = user?.role !== "journalist";

  return (
    <AdminLayout>
      <div data-testid="article-editor">
        <h1 className="font-serif-display text-4xl font-bold">{isNew ? "Nova Notícia" : "Editar Notícia"}</h1>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-4">
            <Field label="Título" required>
              <input type="text" value={form.title} onChange={e => update("title", e.target.value)} onBlur={onTitleBlur} className="w-full border border-zinc-300 px-3 py-2 font-serif-display text-xl" data-testid="editor-title" />
            </Field>
            <Field label="Subtítulo">
              <input type="text" value={form.subtitle} onChange={e => update("subtitle", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-body" data-testid="editor-subtitle" />
            </Field>
            <Field label="Slug (URL)">
              <input type="text" value={form.slug} onChange={e => update("slug", slugify(e.target.value))} className="w-full border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="editor-slug" />
            </Field>
            <Field label="Conteúdo (rich text)" required>
              <RichEditor
                value={form.content}
                onChange={(html) => update("content", html)}
                onRequestImage={async () => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  return new Promise((resolve) => {
                    input.onchange = async () => {
                      const f = input.files?.[0];
                      if (!f) return resolve(null);
                      try {
                        const fd = new FormData(); fd.append("file", f);
                        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        resolve(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
                      } catch { toast.error("Erro no upload"); resolve(null); }
                    };
                    input.click();
                  });
                }}
              />
            </Field>
            <Field label="Imagem de capa">
              <ImageUpload value={form.cover_image} onChange={(v) => update("cover_image", v)} label="Capa" testid="editor-cover" />
            </Field>
            <Field label="Vídeo (URL do YouTube)">
              <input type="url" value={form.video_url} onChange={e => update("video_url", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="editor-video" />
            </Field>
            <Field label="Galeria de imagens">
              <ImageUpload value={imgInput} onChange={(v) => { if (v) { update("images", [...form.images, v]); setImgInput(""); } }} label="Imagem" testid="editor-gallery" />
              <ul className="mt-2 grid grid-cols-3 gap-2">
                {form.images.map((im, i) => (
                  <li key={i} className="relative">
                    <img src={im} alt="" className="w-full aspect-square object-cover" />
                    <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/70 text-white p-1"><Trash2 size={12}/></button>
                  </li>
                ))}
              </ul>
            </Field>

            <details className="border border-zinc-300 p-4">
              <summary className="overline cursor-pointer">SEO</summary>
              <div className="mt-3 space-y-3">
                <Field label="Título SEO"><input type="text" value={form.seo_title} onChange={e => update("seo_title", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="editor-seo-title" /></Field>
                <Field label="Descrição SEO"><textarea rows={3} value={form.seo_description} onChange={e => update("seo_description", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="editor-seo-desc" /></Field>
                <Field label="Palavras-chave (separadas por vírgula)"><input type="text" value={form.seo_keywords} onChange={e => update("seo_keywords", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-sans-ui text-sm" /></Field>
              </div>
            </details>
          </div>

          <aside className="space-y-4">
            <div className="border border-zinc-300 bg-white p-4">
              <p className="overline mb-3">Publicação</p>
              <div className="space-y-2">
                <button onClick={() => save("draft")} className="w-full flex items-center justify-center gap-2 border border-zinc-400 px-4 py-2 font-sans-ui text-sm uppercase tracking-widest font-semibold hover:bg-zinc-100" data-testid="save-draft-btn">
                  <Save size={14}/> Salvar Rascunho
                </button>
                {canPublish && (
                  <button onClick={() => save("published")} className="w-full flex items-center justify-center gap-2 bg-red-700 hover:bg-red-600 text-white px-4 py-2 font-sans-ui text-sm uppercase tracking-widest font-semibold" data-testid="publish-btn">
                    <Send size={14}/> Publicar
                  </button>
                )}
                {canPublish && (
                  <>
                    <DateTimePicker value={form.scheduled_at || ""} onChange={(v) => update("scheduled_at", v)} placeholder="Selecione data e hora" />
                    <button onClick={() => save("scheduled")} disabled={!form.scheduled_at} className="w-full flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-600 text-white px-4 py-2 font-sans-ui text-sm uppercase tracking-widest font-semibold disabled:opacity-50" data-testid="schedule-btn">
                      <Calendar size={14}/> Agendar
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="border border-zinc-300 bg-white p-4 space-y-3">
              <Field label="Categoria">
                <select value={form.category_id || ""} onChange={e => update("category_id", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="editor-category">
                  <option value="">Selecione</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Autor">
                <select value={form.author_id || ""} onChange={e => update("author_id", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 font-sans-ui text-sm" data-testid="editor-author">
                  <option value="">Selecione</option>
                  {authors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </Field>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="feat" checked={form.featured} onChange={e => update("featured", e.target.checked)} data-testid="editor-featured" />
                <label htmlFor="feat" className="font-sans-ui text-sm">Notícia em destaque</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="brk" checked={form.breaking} onChange={e => update("breaking", e.target.checked)} data-testid="editor-breaking" />
                <label htmlFor="brk" className="font-sans-ui text-sm">Notícia urgente (ticker)</label>
              </div>
            </div>

            <div className="border border-zinc-300 bg-white p-4">
              <p className="overline mb-2">Tags</p>
              <div className="flex gap-2">
                <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} className="flex-1 border border-zinc-300 px-3 py-1.5 font-sans-ui text-sm" />
                <button onClick={addTag} className="bg-zinc-200 px-3 font-sans-ui text-xs">Add</button>
              </div>
              <ul className="mt-2 flex flex-wrap gap-1">
                {form.tags.map((t, i) => (
                  <li key={i} onClick={() => removeTag(i)} className="bg-zinc-100 px-2 py-0.5 text-xs font-sans-ui cursor-pointer hover:bg-red-100">#{t} ×</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </AdminLayout>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="overline">{label} {required && <span className="text-red-700">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
